import React, { useState, useEffect } from "react";
import SectionTitle from "@others/title-section.layout";
import {
    Box,
    Container,
    Typography,
    Paper,
    Alert,
    AlertTitle,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faInfoCircle,
    faShieldAlt,
    faCheckCircle,
    faBan,
    faUserShield,
    faQrcode,
    faMobileAlt,
} from "@fortawesome/free-solid-svg-icons";
import { DonorForm } from "../components/forms/donor.form";

const InfoCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const WeroCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
    marginTop: theme.spacing(4),
}));

const StepBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const DonatePage: React.FC = () => {
    const [donationSubmitted, setDonationSubmitted] = useState(false);

    // Informations Wero du candidat
    const weroInfo = {
        displayName: "Jean-Phillipe GUIBERT",
        phoneNumber: "+33 6 99 54 67 47",
        iban: "FR76 XXXX XXXX XXXX XXXX XXXX XXX",
    };

    // Scroll to top when donation is submitted
    useEffect(() => {
        if (donationSubmitted) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [donationSubmitted]);

    return (
        <Container sx={{ pb: 6 }}>
            <SectionTitle title={"SOUTENIR LA CAMPAGNE VLD 2026"} />

            <Box mt={4} mb={4}>
                {!donationSubmitted ? (
                    <>
                        {/* Section d'information principale */}
                        <InfoCard elevation={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    style={{ fontSize: '32px', color: '#667eea' }}
                                />
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                                    Pourquoi faire un don ?
                                </Typography>
                            </Box>
                            <Typography variant="body1" paragraph>
                                Votre soutien financier est essentiel pour mener à bien notre campagne municipale 2026.
                                Chaque contribution, quelle que soit son montant, nous permet de porter nos idées et notre
                                projet pour notre commune.
                            </Typography>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <AlertTitle>Transparence et conformité</AlertTitle>
                                Tous les dons sont déclarés et gérés dans le strict respect de la réglementation
                                électorale française.
                            </Alert>
                        </InfoCard>

                        {/* Qui peut donner */}
                        <InfoCard elevation={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <FontAwesomeIcon
                                    icon={faShieldAlt}
                                    style={{ fontSize: '32px', color: '#28a745' }}
                                />
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                                    Qui peut faire un don ?
                                </Typography>
                            </Box>

                            <Typography variant="h6" sx={{ color: '#28a745', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={faCheckCircle} />
                                Peuvent donner :
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Les personnes physiques"
                                        secondary="Citoyens français ou résidents permanents"
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#28a745' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Montant limité"
                                        secondary="Dons compris entre 5€ et 500€ (multiples de 5€)"
                                    />
                                </ListItem>
                            </List>

                            <Divider sx={{ my: 3 }} />

                            <Typography variant="h6" sx={{ color: '#dc3545', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FontAwesomeIcon icon={faBan} />
                                Ne peuvent PAS donner :
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faBan} style={{ color: '#dc3545' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Les personnes morales"
                                        secondary="Entreprises, sociétés, SARL, SAS, etc."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faBan} style={{ color: '#dc3545' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Les associations"
                                        secondary="Associations loi 1901, fondations, etc."
                                    />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faBan} style={{ color: '#dc3545' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Les organisations professionnelles"
                                        secondary="Syndicats, groupements, etc."
                                    />
                                </ListItem>
                            </List>

                            <Alert severity="warning" sx={{ mt: 3 }}>
                                <AlertTitle>Attention</AlertTitle>
                                Les dons provenant de personnes morales (entreprises, associations) sont strictement
                                interdits par la loi et ne pourront pas être acceptés.
                            </Alert>
                        </InfoCard>

                        {/* Identification obligatoire */}
                        <InfoCard elevation={2}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                <FontAwesomeIcon
                                    icon={faUserShield}
                                    style={{ fontSize: '32px', color: '#667eea' }}
                                />
                                <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                                    Pourquoi toutes ces informations ?
                                </Typography>
                            </Box>
                            <Typography variant="body1" paragraph>
                                Pour une campagne municipale, la réglementation française impose au candidat d'être
                                en mesure d'<strong>identifier précisément chaque donateur</strong>. Cette obligation légale
                                garantit la transparence du financement de la campagne.
                            </Typography>
                            <Typography variant="body1">
                                C'est la raison pour laquelle nous vous demandons vos informations personnelles complètes :
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#667eea' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Prénom et Nom" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#667eea' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Adresse complète (rue, code postal, ville)" />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#667eea' }} />
                                    </ListItemIcon>
                                    <ListItemText primary="Adresse email" />
                                </ListItem>
                            </List>
                            <Alert severity="success" sx={{ mt: 2 }}>
                                <AlertTitle>Protection de vos données</AlertTitle>
                                Vos données personnelles sont traitées de manière confidentielle et ne seront utilisées
                                que dans le cadre de la campagne électorale, conformément au RGPD.
                            </Alert>
                        </InfoCard>

                        {/* Formulaire de don */}
                        <Box sx={{ mt: 5 }}>
                            <DonorForm onSuccess={() => setDonationSubmitted(true)} />
                        </Box>
                    </>
                ) : (
                    <>
                        {/* Message de succès et instructions Wero */}
                        <InfoCard elevation={3}>
                            <Box sx={{ textAlign: 'center', mb: 4 }}>
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    style={{ fontSize: '80px', color: '#28a745', marginBottom: '16px' }}
                                />
                                <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#28a745' }}>
                                    Formulaire enregistré !
                                </Typography>
                                <Typography variant="h6" color="text.secondary" paragraph>
                                    Merci d'avoir rempli le formulaire de don.
                                </Typography>
                            </Box>

                            <Alert severity="info" sx={{ mb: 4 }}>
                                <AlertTitle sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                    Il vous reste une dernière étape
                                </AlertTitle>
                                <Typography variant="body1">
                                    Pour finaliser votre don, vous devez maintenant effectuer le paiement via <strong>Wero</strong>.
                                </Typography>
                            </Alert>

                            <Divider sx={{ my: 4 }} />

                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                                Comment effectuer votre don avec Wero ?
                            </Typography>

                            <Box sx={{ mb: 4 }}>
                                <StepBox>
                                    <Chip label="1" color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <FontAwesomeIcon icon={faMobileAlt} style={{ marginRight: '8px' }} />
                                            Ouvrez l'application Wero
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Lancez l'application Wero sur votre smartphone
                                        </Typography>
                                    </Box>
                                </StepBox>

                                <StepBox>
                                    <Chip label="2" color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <FontAwesomeIcon icon={faQrcode} style={{ marginRight: '8px' }} />
                                            Scannez ou saisissez les informations
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Utilisez les informations ci-dessous pour effectuer le paiement
                                        </Typography>
                                    </Box>
                                </StepBox>

                                <StepBox>
                                    <Chip label="3" color="primary" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }} />
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Validez le paiement
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Confirmez le montant et validez la transaction
                                        </Typography>
                                    </Box>
                                </StepBox>
                            </Box>
                        </InfoCard>

                        {/* Carte Wero */}
                        <WeroCard elevation={6}>
                            <CardContent>
                                <Box sx={{ textAlign: 'center', mb: 3 }}>
                                    <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        Informations Wero
                                    </Typography>
                                    <Typography variant="body1">
                                        Utilisez ces informations pour effectuer votre paiement
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 3, backgroundColor: 'rgba(255,255,255,0.3)' }} />

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
                                            Nom du bénéficiaire
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                            {weroInfo.displayName}
                                        </Typography>
                                    </Box>

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ opacity: 0.9, mb: 1 }}>
                                            Numéro de téléphone
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}>
                                            {weroInfo.phoneNumber}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Alert severity="warning" sx={{ mt: 4, backgroundColor: 'rgba(255,255,255,0.95)' }}>
                                    <AlertTitle>Important</AlertTitle>
                                    Assurez-vous que le montant transféré correspond bien au montant indiqué dans le formulaire.
                                </Alert>
                            </CardContent>
                        </WeroCard>

                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                Vous recevrez un email de confirmation une fois votre paiement traité.
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default DonatePage;